import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Banknote, ExternalLink, Loader, AlertCircle } from "lucide-react";

export default function PayoutCard({ earnings }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");

  const load = () => api.get("/connect/status").then(r => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  useEffect(() => { load(); }, []);

  const startOnboarding = async () => {
    setBusy("onboard"); setMsg("");
    try {
      const r = await api.post("/connect/onboard", {
        return_url: window.location.origin + "/dashboard?connect=done",
        refresh_url: window.location.origin + "/dashboard?connect=refresh",
      });
      window.location.href = r.data.url;
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Onboarding failed");
      setBusy("");
    }
  };

  const openDashboard = async () => {
    setBusy("dash"); setMsg("");
    try {
      const r = await api.post("/connect/login_link");
      window.open(r.data.url, "_blank", "noopener");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not open dashboard");
    } finally { setBusy(""); }
  };

  const cashOut = async () => {
    setBusy("payout"); setMsg("");
    try {
      const r = await api.post("/connect/payout");
      const mock = r.data.mock ? " (PREVIEW — real transfer requires platform owner to add STRIPE_CONNECT_KEY)" : "";
      setMsg(`Sent $${r.data.amount_usd.toFixed(2)} to your Stripe Express balance${mock}`);
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Payout failed");
    } finally { setBusy(""); }
  };

  if (!status) return <div className="sc-card p-6"><Loader className="w-5 h-5 animate-spin text-zinc-400" /></div>;

  const totalPending = earnings?.total || 0;

  return (
    <div className="sc-card p-6 space-y-4" data-testid="payout-card">
      <div className="flex items-center gap-2">
        <Banknote className="w-5 h-5 text-[#10B981]" />
        <div className="sc-heading text-xl font-bold">Stripe Payouts</div>
      </div>

      {!status.configured && (
        <div className="flex items-start gap-2 text-xs p-3 border border-[#F59E0B]/30 bg-[#F59E0B]/5 rounded-sm">
          <AlertCircle className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-[#F59E0B] font-bold mb-1">Connect not configured</div>
            <div className="text-zinc-300">Platform owner must add their own Stripe API key (`STRIPE_CONNECT_KEY`) to enable real bank payouts. Until then, "Cash Out" uses preview mode (no real money moves).</div>
          </div>
        </div>
      )}

      <div>
        <div className="sc-label">Pending payout</div>
        <div className="text-3xl font-bold text-[#10B981] mt-1">${totalPending.toFixed(2)}</div>
        <div className="text-[10px] text-zinc-500 mt-1">70% of every sub + tip routed to you.</div>
      </div>

      {!status.stripe_account_id && status.configured && (
        <button data-testid="connect-onboard" disabled={busy === "onboard"} onClick={startOnboarding} className="sc-btn w-full">
          {busy === "onboard" ? "Redirecting..." : "Onboard with Stripe Express"}
        </button>
      )}

      {status.stripe_account_id && !status.payouts_enabled && (
        <div className="text-xs text-zinc-400 p-3 border border-white/10 rounded-sm">
          Stripe account created but not yet payouts-enabled. Complete onboarding.
          <button data-testid="connect-resume" onClick={startOnboarding} className="sc-btn-ghost text-xs ml-2 mt-2">Resume Onboarding</button>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <button data-testid="connect-cashout" disabled={busy === "payout" || totalPending <= 0} onClick={cashOut} className="sc-btn text-sm flex-1">
          {busy === "payout" ? "Processing..." : "Cash Out"}
        </button>
        {status.stripe_account_id && status.configured && (
          <button data-testid="connect-dashboard" disabled={busy === "dash"} onClick={openDashboard} className="sc-btn-ghost text-sm flex items-center gap-1.5">
            <ExternalLink className="w-3.5 h-3.5" /> Stripe
          </button>
        )}
      </div>

      {msg && <div className="text-xs text-[#10B981] p-2 border border-[#10B981]/30 bg-[#10B981]/5 rounded-sm">{msg}</div>}
    </div>
  );
}
